import React, { useState, useCallback } from 'react';
import { map, filter, propEq, assoc } from 'ramda';
import styled, { css } from 'styled-components';
import CollapsibleChevronBlock from '~/components/molecule/CollapsibleChevronBlock';
import JustificationContainer from '~/components/atom/JustificationContainer';
import alterById from '~/util/alterById/index';
import AnimatedCheckbox from '~/components/molecule/AnimatedCheckbox';
import { Heading4, Label, Variant } from '~/components/atom/Typography';
import GridContainer from '~/components/atom/GridContainer';
import withIconTooltip from '~/hocs/withIconTooltip';
import KenmerkRow from './components/KenmerkRow';
import { RealworksRelatieSyncType } from '~/graphql/types';
import Icon from '~/components/atom/Icon';
import type { Kenmerk } from '../Synchronization/types';

const text = {
  kenmerkLabel: 'Relaties met kenmerk',
  tagLabel: 'Tag toevoegen in BNDL',
  [RealworksRelatieSyncType.Full]: {
    realworksKenmerkTooltip: 'Alle relaties worden gesynchroniseerd.',
    tagTooltip:
      'Deze tag wordt toegevoegd aan het contact in BNDL in plaats van deze kenmerk',
  },
  [RealworksRelatieSyncType.LimitToKenmerken]: {
    realworksKenmerkTooltip:
      'Relaties met geselecteerde kenmerken worden gesynchroniseerd.',
    tagTooltip: `Deze tag wordt aan het contact in BNDL toegevoegd als de gesynchroniseerde relatie 
    dit kenmerk in Realworks heeft — ook wanneer dit kenmerk hier niet is geselecteerd.`,
  },
  [RealworksRelatieSyncType.FullExcludingKenmerken]: {
    realworksKenmerkTooltip:
      'Relaties met geselecteerde kenmerken worden niet gesynchroniseerd.',
    tagTooltip:
      'Deze tag wordt toegevoegd aan het contact in BNDL in plaats van deze kenmerk',
  },
};

type Field = 'check' | 'tag';

export type Props = {
  /** Group name */
  name: string;
  /** All kenmerken within this group */
  kenmerken: Array<Kenmerk>;
  /** Whether the group is selectable */
  selectable: boolean;
  /** The sync type */
  syncType: RealworksRelatieSyncType;
  /** The function to call when the group is changed */
  onChange: (args: { kenmerkList: Array<Kenmerk> }) => void;
};

const KenmerkGroup: React.FCC<Props> = ({
  dataTestId,
  kenmerken,
  name,
  selectable,
  syncType,
  onChange: onParentChange,
  ...rest
}) => {
  const checkedAmount: number = filter(
    propEq(true, 'checked'),
    kenmerken,
  ).length;

  const isGroupChecked = checkedAmount === kenmerken.length;
  const partiallyChecked = !isGroupChecked && checkedAmount > 0;

  const [contentOpen, setContentOpen] = useState<boolean>(true);

  const onChange = useCallback(
    (id: number, value: string | boolean | null, field: Field) => {
      const updated = alterById(id, field, value, kenmerken);
      onParentChange({ kenmerkList: updated });
    },
    [kenmerken, onParentChange],
  );

  const markAllCheckboxes = useCallback(
    (value: boolean) =>
      onParentChange({ kenmerkList: map(assoc('checked', value), kenmerken) }),
    [kenmerken, onParentChange],
  );

  const hideLabel =
    syncType === RealworksRelatieSyncType.FullExcludingKenmerken &&
    isGroupChecked;

  return (
    <CollapsibleChevronBlock
      data-testid={dataTestId}
      header={
        <AnimatedCheckbox
          label={
            <Label size="base" margin={[null]}>
              {name}
            </Label>
          }
          name={`${name}-group-checkbox`}
          value={isGroupChecked || partiallyChecked}
          icon={partiallyChecked ? 'minus' : undefined}
          selectable={selectable}
          onChange={() => {
            setContentOpen(true);
            markAllCheckboxes(
              partiallyChecked ? isGroupChecked : !isGroupChecked,
            );
          }}
          appearance="primary"
        />
      }
      headerClickable={false}
      variant="primary"
      isOpen={contentOpen}
      onToggle={() => setContentOpen(!contentOpen)}
      {...rest}
    >
      <ChildrenContainer>
        <GridContainer gap="base" columns="1fr 1fr">
          <JustificationContainer direction="column">
            <LabelWithTooltip
              size="s"
              variant={Variant.primary}
              tooltipMessage={text[syncType].realworksKenmerkTooltip}
            >
              <JustificationContainer direction="row" gap="xxs" align="center">
                <Icon name="users" strokeWidth={2.3} />
                {text.kenmerkLabel}
              </JustificationContainer>
            </LabelWithTooltip>
          </JustificationContainer>
          <JustificationContainer direction="column">
            {!hideLabel && (
              <LabelWithTooltip
                size="s"
                variant={Variant.primary}
                tooltipMessage={text[syncType].tagTooltip}
              >
                <JustificationContainer
                  direction="row"
                  gap="xxs"
                  align="center"
                >
                  <Icon name="tag" strokeWidth={2.3} />
                  {text.tagLabel}
                </JustificationContainer>
              </LabelWithTooltip>
            )}
          </JustificationContainer>

          {kenmerken.map(({ description, id, checked, tag }) => (
            <KenmerkRow
              row={{
                description,
                id,
                checked,
                tag,
              }}
              key={`key-${id}`}
              onChange={(value, field: Field) => onChange(id, value, field)}
              selectable={selectable}
              syncType={syncType}
            />
          ))}
        </GridContainer>
      </ChildrenContainer>
    </CollapsibleChevronBlock>
  );
};

const LabelWithTooltip = withIconTooltip(Heading4, {
  variant: Variant.primary,
  margin: [null],
});

const ChildrenContainer = styled.div<{}>`
  ${({ theme }) => css`
    padding: ${theme.space('m')} ${theme.space('l')};
    background-color: ${theme.color('white')};
    border-radius: ${theme.getTokens().border.radius.base};
  `}
`;

export default KenmerkGroup;
